// The frame's one boot call (shell-ia §8.2; SPEC §4.3): `GET /api/econic/shell` returns who is signed in, the session, the preferences, the
// departments, the counts for the nav and the bell, and the environment. It replaces the nine calls the os frame made on every load
// (`/api/matters?status=closed&take=200` among them). WP-SHELL.
//
// While a stack does not serve the endpoint yet (older API), the hook reports `unavailable` and every consumer shows what it can read
// itself, never a guessed count.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, useApi, useAuth, useInvalidateOn } from "@ncp/api-client";
import type { ShellSummary } from "@ncp/assistant-sdk/contracts";

export const SHELL_QUERY_KEY = ["econic", "shell"] as const;
export const INBOX_QUERY_KEY = ["econic", "inbox"] as const;

export interface ShellSummaryState {
  data: ShellSummary | null;
  loading: boolean;
  /** The stack does not serve `/api/econic/shell` (404/405) or it failed: consumers fall back to their own reads. */
  unavailable: boolean;
}

const missing = (e: unknown) => e instanceof ApiError && (e.status === 404 || e.status === 405 || e.status === 501);

export function useShellSummary(): ShellSummaryState {
  const api = useApi();
  const { isAuthenticated, user } = useAuth();
  const q = useQuery({
    queryKey: [...SHELL_QUERY_KEY, user?.id ?? null],
    queryFn: () => api.get<ShellSummary>("/api/econic/shell"),
    enabled: isAuthenticated,
    staleTime: 60_000,
    // Counts move through realtime events (below); this is the safety net, never more often than every 5 minutes (shell-ia §8.2).
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: (n, e) => !missing(e) && n < 1,
  });
  return { data: q.data ?? null, loading: q.isLoading, unavailable: q.isError };
}

/** Keeps the counts and the bell current: a proposal, a notification, a routine run or a preference change invalidates them. */
export function useShellRealtime(): void {
  useInvalidateOn(
    // `platform.event` (page rebuilds, source runs) is left out on purpose: it fires often and moves none of these counts.
    ["proposal.created", "proposal.decided", "proposal.approved", "proposal.rejected", "notification.created", "watcher.ran", "ui.pref.changed", "todo.created"],
    [[...SHELL_QUERY_KEY], [...INBOX_QUERY_KEY]],
  );
}

/** Refetches the shell bundle now (after a department switch or a preference write the server answered). */
export function useRefreshShell(): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: [...SHELL_QUERY_KEY] });
}

/**
 * How the agent's model route reads on this host (the classification strip, the agent's route line, "What the agent can see"):
 * `zdr` only where the server enforces zero data retention on external models, `offline` where no external model is called, else
 * `external` — also while the bundle has not answered, so the frame never claims a zero retention it has not been told about.
 */
export type AiRoute = "zdr" | "external" | "offline";
export function useAiRoute(): AiRoute {
  const ai = useShellSummary().data?.ai;
  if (ai?.external === false) return "offline";
  return ai?.zeroRetention ? "zdr" : "external";
}

/** The locale-key suffix of a sentence that depends on the route: "" (external), "Zdr" or "Offline". */
export const aiRouteSuffix = (route: AiRoute): string => (route === "zdr" ? "Zdr" : route === "offline" ? "Offline" : "");
